import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { Link } from "react-router-dom";
import { Mail, Network, ArrowUpRight, CheckCircle2, TrendingUp, Shield, Sparkles, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, lazy, Suspense, useEffect, useRef } from "react";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import { Helmet } from "react-helmet";

// Below-the-fold (Pricing + Beta + CTA + Footer) loads after first paint
// or when the user scrolls within 400px of it. Saves ~40KB on initial render.
const HomeBelowFold = lazy(() => import("@/components/landing/HomeBelowFold"));

// Feature lists for each plan
const PLAN_FEATURES = {
  starter: ["50 leads per month", "Manual lead entry & CSV import", "Basic contact management", "5 email templates", "100 emails per month"],
  growth: ["500 leads per month", "50 Arrow AI discoveries", "20 web scrapes per month", "150 email verifications", "Unlimited templates", "1,000 emails per month", "75 domain searches/month"],
  enterprise: ["Unlimited leads", "Unlimited Arrow AI discoveries", "Unlimited web scrapes", "750 email verifications", "Pilot workflow automation", "Unlimited emails", "300 domain searches/month", "Priority support"]
};

// Price IDs
const PRICE_IDS = {
  growth: {
    monthly: "price_1SdGCnGx2ak1DuREJI6Vk1uf",
    annual: "price_1SdGGfGx2ak1DuREAIolR00A"
  },
  enterprise: {
    monthly: "price_1SdGCxGx2ak1DuRElceQ2T3z",
    annual: "price_1SdGGhGx2ak1DuREZcRXIMQD"
  }
};
const Home = () => {
  const {
    t
  } = useTranslation();
  const [isAnnual, setIsAnnual] = useState(false);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [showBelowFold, setShowBelowFold] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Idle-load after first paint as a safety net
    const idle = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 800));
    const handle = idle(() => setShowBelowFold(true));

    // Also load when user scrolls near it
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShowBelowFold(true);
          obs.disconnect();
        }
      },
      { rootMargin: "400px" }
    );
    if (sentinelRef.current) obs.observe(sentinelRef.current);
    return () => {
      obs.disconnect();
      if ((window as any).cancelIdleCallback && typeof handle === "number") {
        (window as any).cancelIdleCallback(handle);
      }
    };
  }, []);

  const handleGetStarted = async (planId: string) => {
    if (planId === "starter") {
      window.location.href = "/auth";
      return;
    }

    // Redirect to checkout page
    window.location.href = `/checkout?plan=${planId}&billing=${isAnnual ? 'annual' : 'monthly'}`;
  };
  const starterFeatures = t('home.plans.starter.features', { returnObjects: true }) as string[];
  const growthFeatures = t('home.plans.growth.features', { returnObjects: true }) as string[];
  const enterpriseFeatures = t('home.plans.enterprise.features', { returnObjects: true }) as string[];

  const tiers = [{
    id: "starter",
    name: t('home.plans.starter.name'),
    price: t('common.free'),
    originalPrice: null,
    description: t('home.plans.starter.description'),
    features: Array.isArray(starterFeatures) ? starterFeatures : PLAN_FEATURES.starter,
    variant: "default" as const
  }, {
    id: "growth",
    name: t('home.plans.growth.name'),
    price: isAnnual ? "$39" : "$49",
    originalPrice: isAnnual ? "$55" : "$69",
    description: t('home.plans.growth.description'),
    features: Array.isArray(growthFeatures) ? growthFeatures : PLAN_FEATURES.growth,
    variant: "gradient" as const,
    popular: true
  }, {
    id: "enterprise",
    name: t('home.plans.enterprise.name'),
    price: isAnnual ? "$119" : "$149",
    originalPrice: isAnnual ? "$169" : "$209",
    description: t('home.plans.enterprise.description'),
    features: Array.isArray(enterpriseFeatures) ? enterpriseFeatures : PLAN_FEATURES.enterprise,
    variant: "accent" as const
  }];
  const features = [{
    icon: Mail,
    title: t('home.features.smartEmail.title'),
    description: t('home.features.smartEmail.description')
  }, {
    icon: Network,
    title: t('home.features.livingNetwork.title'),
    description: t('home.features.livingNetwork.description')
  }, {
    icon: ArrowUpRight,
    title: t('home.features.arrowAI.title'),
    description: t('home.features.arrowAI.description')
  }, {
    icon: CheckCircle2,
    title: t('home.features.emailVerification.title'),
    description: t('home.features.emailVerification.description')
  }, {
    icon: TrendingUp,
    title: t('home.features.pilotAutomation.title'),
    description: t('home.features.pilotAutomation.description')
  }, {
    icon: Shield,
    title: t('home.features.dataPrivacy.title'),
    description: t('home.features.dataPrivacy.description')
  }];
  return <div className="min-h-screen bg-white">
      <Helmet>
        <title>Arrowlink - Turn Emails Into Living Connections</title>
        <meta name="description" content="Arrowlink turns your inbox into a living network: find leads, personalize outreach, track replies and close deals from one platform." />
        <link rel="canonical" href="https://arrowlink.ai/" />
        <meta property="og:title" content="Arrowlink - Turn Emails Into Living Connections" />
        <meta property="og:description" content="Find leads, personalize outreach, track replies and close deals from one platform." />
        <meta property="og:url" content="https://arrowlink.ai/" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 mt-[2vh] md:mt-[5vh] pb-12 md:pb-24 px-0 overflow-hidden bg-gradient-to-b from-white via-blue-50/30 to-gray-50">
        {/* Ambient orbs — mobile-engaging visual depth */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-primary/10 blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
          <div className="absolute top-40 -left-20 w-72 h-72 rounded-full bg-accent/10 blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />
        </div>
        <div className="mobile-container md:container md:mx-auto relative z-10">
          {/* No entry animation on the hero: the H1 is the LCP element and any
              initial opacity:0 state delays Largest Contentful Paint. */}
          <div className="max-w-5xl mx-auto text-center space-y-4 md:space-y-6">
            <div className="space-y-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-[11px] md:text-sm font-semibold text-primary tracking-[0.18em] md:tracking-[0.3em] uppercase">
                <Sparkles className="h-3 w-3 md:h-3.5 md:w-3.5" />
                {t('home.hero.badge', 'LIVING CONNECTIONS')}
              </span>
              <h1 className="text-[2.25rem] leading-[1.05] md:text-8xl md:leading-none font-black text-foreground tracking-tight px-1">
                {t('home.hero.title')}
              </h1>
            </div>
            
            <p className="text-base md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed pt-1 md:pt-4 px-2 md:px-0">
              {t('home.hero.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-2.5 md:gap-4 justify-center items-center pt-3 md:pt-4 px-2 md:px-0">
              <Link to="/auth" className="w-full sm:w-auto">
                <Button variant="gradient" size="lg" className="w-full sm:w-auto text-base md:text-lg px-8 md:px-10 py-6 md:py-6 rounded-full shadow-medium">
                  {t('home.hero.cta')}
                </Button>
              </Link>
              <Link to="/auth?demo=true" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" className="w-full sm:w-auto text-base md:text-lg px-8 md:px-10 py-6 md:py-6 rounded-full">
                  {t('home.hero.liveDemo', 'Live Demo')}
                </Button>
              </Link>
            </div>

            {/* Mobile trust strip */}
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 pt-4 md:pt-6 text-[11px] md:text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> Free forever plan
              </span>
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> No credit card
              </span>
              <span className="hidden md:inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> Setup in 60 seconds
              </span>
            </div>
            <div className="flex items-center justify-center gap-1.5 pt-1 md:pt-2 text-[11px] md:text-xs text-muted-foreground">
              <div className="flex">
                {[0,1,2,3,4].map(i => <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />)}
              </div>
              <span>Trusted by 500+ founders & agencies</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10 md:py-20 px-0 bg-white">
        <div className="mobile-container md:container md:mx-auto">
          <div className="text-center mb-8 md:mb-16 animate-slide-up">
            <p className="text-[11px] md:text-xs font-semibold text-primary tracking-[0.2em] md:tracking-[0.3em] uppercase mb-2 md:mb-4">
              {t('home.features.badge')}
            </p>
            <h2 className="text-[1.75rem] leading-tight md:text-5xl font-black mb-4 text-foreground tracking-tight">
              {t('home.features.title')}
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2.5 md:gap-6 max-w-6xl mx-auto">
            {features.map((feature, index) => <div key={index} className="group p-4 md:p-6 rounded-2xl bg-gradient-blue hover:bg-white border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-medium animate-slide-up active:scale-[0.98] touch-manipulation" style={{
            animationDelay: `${index * 100}ms`
          }}>
                <div className="mb-2.5 md:mb-4 inline-flex p-2.5 md:p-3 rounded-xl bg-white group-hover:bg-gradient-hero shadow-soft group-hover:shadow-medium transition-all">
                  <feature.icon className="h-5 w-5 md:h-6 md:w-6 text-primary group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-[0.95rem] md:text-xl font-bold mb-1.5 md:mb-2 text-foreground leading-snug">{feature.title}</h3>
                <p className="text-muted-foreground text-[12px] md:text-sm leading-relaxed line-clamp-4 md:line-clamp-none">{feature.description}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Sentinel to trigger lazy load just before below-the-fold scrolls in */}
      <div ref={sentinelRef} aria-hidden="true" />

      {showBelowFold && (
        <Suspense fallback={null}>
          <HomeBelowFold
            tiers={tiers}
            isAnnual={isAnnual}
            setIsAnnual={setIsAnnual}
            isLoading={isLoading}
            onGetStarted={handleGetStarted}
          />
        </Suspense>
      )}

      <MobileStickyCTA label={t('home.hero.cta')} to="/auth" />
    </div>;
};
export default Home;